import { Card } from '@trafiflow/shared-ui'
import { Bookmark, BookmarkX, Search, Grid, List, Loader2, Sparkles, Play, Image as ImageIcon } from 'lucide-react'
import { useState, useEffect } from 'react'
import { fbAdsService, type FbAd } from '../../../../services/fb-ads'
import { AdDetailModal } from './AdDetailModal'
import { isVideoUrl } from './media'

function SavedAdCard({ ad, viewMode, onClick, onRemove }: { ad: FbAd; viewMode: 'grid' | 'list'; onClick: () => void; onRemove: (e: React.MouseEvent) => void }) {
  const [imageError, setImageError] = useState(false)
  const primaryMedia = ad.media_urls?.[0] || null
  const hasVideo = (ad.media_urls || []).some(isVideoUrl) || ad.media_type === 'video'
  const isVideo = isVideoUrl(primaryMedia)

  const renderMedia = () => {
    if (!primaryMedia || imageError) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          {hasVideo ? (
            <Play className="w-12 h-12 text-tf-text-muted" />
          ) : (
            <ImageIcon className="w-12 h-12 text-tf-text-muted" />
          )}
        </div>
      )
    }

    if (isVideo) {
      return (
        <div className="relative w-full h-full">
          <video
            src={primaryMedia}
            className="w-full h-full object-cover"
            muted
            preload="metadata"
            onError={() => setImageError(true)}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-black/30 flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="relative w-full h-full">
        <img
          src={primaryMedia}
          alt={ad.page_name}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
        {hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-black/30 flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
          </div>
        )}
      </div>
    )
  }

  const platformBadges = ad.platforms?.length > 0 ? ad.platforms : ['facebook']

  return (
    <Card className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${viewMode === 'list' ? 'flex' : ''}`} onClick={onClick}>
      <div className={`relative ${viewMode === 'list' ? 'w-48 h-32 flex-shrink-0' : 'aspect-video'}`}>
        {renderMedia()}
        {/* Remove Button */}
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 p-2 rounded-full bg-tf-critical text-white hover:bg-red-600 transition-colors"
          title="Remove from saved"
        >
          <BookmarkX className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 flex-1">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-600">
            {ad.page_name?.charAt(0).toUpperCase() || 'A'}
          </div>
          <span className="text-sm font-medium text-tf-text truncate">{ad.page_name || 'Unknown Brand'}</span>
        </div>
        {ad.ad_text && (
          <p className="text-sm text-tf-text-muted line-clamp-2 mb-3">
            {ad.ad_text}
          </p>
        )}
        <div className="flex items-center justify-between text-xs text-tf-text-muted">
          <div className="flex gap-1">
            {platformBadges.slice(0, 2).map((platform) => (
              <span key={platform} className="px-2 py-1 bg-tf-bg-hover rounded capitalize">
                {platform}
              </span>
            ))}
            {platformBadges.length > 2 && (
              <span className="px-2 py-1 bg-tf-bg-hover rounded">
                +{platformBadges.length - 2}
              </span>
            )}
          </div>
          <span className="capitalize">{ad.media_type}</span>
        </div>
      </div>
    </Card>
  )
}

export function SavedAds() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [savedAds, setSavedAds] = useState<FbAd[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAd, setSelectedAd] = useState<FbAd | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    loadSavedAds()
  }, [])

  const loadSavedAds = async () => {
    try {
      setLoading(true)
      // Get saved ad IDs from localStorage
      const savedIdsJson = localStorage.getItem('savedAdIds')
      if (!savedIdsJson) {
        setSavedAds([])
        return
      }

      const savedIds: string[] = JSON.parse(savedIdsJson)
      if (savedIds.length === 0) {
        setSavedAds([])
        return
      }

      // Fetch all ads and filter by saved IDs
      const allAds = await fbAdsService.listAds()
      const saved = allAds.filter(ad => savedIds.includes(ad.ad_id))
      setSavedAds(saved)
    } catch (err) {
      console.error('[SavedAds] Error loading saved ads:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveAd = (ad: FbAd, e: React.MouseEvent) => {
    e.stopPropagation()
    const savedIdsJson = localStorage.getItem('savedAdIds')
    if (savedIdsJson) {
      const savedIds: string[] = JSON.parse(savedIdsJson)
      const newSavedIds = savedIds.filter(id => id !== ad.ad_id)
      localStorage.setItem('savedAdIds', JSON.stringify(newSavedIds))
      setSavedAds(prev => prev.filter(a => a.ad_id !== ad.ad_id))
    }
  }

  const handleAdClick = (ad: FbAd) => {
    setSelectedAd(ad)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedAd(null)
  }

  const filteredAds = savedAds.filter((ad) => {
    if (!searchTerm) return true
    return (
      ad.page_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.ad_text?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Bookmark className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-tf-text">Saved Ads</h1>
        </div>
        <p className="text-tf-text-muted">
          Your bookmarked ads for reference and inspiration
        </p>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tf-text-muted" />
          <input
            type="text"
            placeholder="Search saved ads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex border border-tf-border rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2.5 ${viewMode === 'grid' ? 'bg-tf-feedback-info-bg text-blue-600' : 'hover:bg-tf-bg-muted'}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2.5 ${viewMode === 'list' ? 'bg-tf-feedback-info-bg text-blue-600' : 'hover:bg-tf-bg-muted'}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-4" />
          <p className="text-tf-text-muted">Loading saved ads...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && savedAds.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <Sparkles className="w-12 h-12 text-tf-text-muted mb-4" />
          <p className="text-tf-text font-medium mb-2">No saved ads yet</p>
          <p className="text-tf-text-muted text-sm text-center max-w-md">
            Browse the Ads Research page and click the bookmark icon to save ads for later reference.
          </p>
        </div>
      )}

      {/* Ads Grid */}
      {!loading && filteredAds.length > 0 && (
        <>
          <p className="text-sm text-tf-text-muted mb-4">
            {filteredAds.length} saved ad{filteredAds.length !== 1 ? 's' : ''}
          </p>
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                : 'space-y-4'
            }
          >
            {filteredAds.map((ad) => (
              <SavedAdCard
                key={ad.ad_id}
                ad={ad}
                viewMode={viewMode}
                onClick={() => handleAdClick(ad)}
                onRemove={(e) => handleRemoveAd(ad, e)}
              />
            ))}
          </div>
        </>
      )}

      {/* No results from search */}
      {!loading && savedAds.length > 0 && filteredAds.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <Search className="w-12 h-12 text-tf-text-muted mb-4" />
          <p className="text-tf-text font-medium mb-2">No matching ads</p>
          <p className="text-tf-text-muted text-sm">Try a different search term.</p>
        </div>
      )}

      {/* Ad Detail Modal */}
      <AdDetailModal 
        ad={selectedAd} 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
      />
    </div>
  )
}

export default SavedAds
